import { memo } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { NoteColor } from '@/enums/noteColor';
import { NOTE_PALETTE } from '@/constants/notes';
import styles from './Note.module.css';

interface Props {
  readonly color: NoteColor;
  readonly onColorChange: (color: NoteColor) => void;
  readonly onDelete: () => void;
}

export const NoteHeader = memo(function NoteHeader({ color, onColorChange, onDelete }: Props) {
  // Stop propagation on the interactive controls only — leaving the rest of the
  // header draggable (like a window title bar).
  const stopPointer = (e: ReactPointerEvent) => e.stopPropagation();

  return (
    <div className={styles.header}>
      <div className={styles.colors} role="radiogroup" aria-label="Note color">
        {NOTE_PALETTE.map((c) => (
          <button
            key={c}
            type="button"
            className={`${styles.colorDot} ${styles[`color_${c}`]} ${c === color ? styles.colorDotActive : ''}`}
            role="radio"
            aria-checked={c === color}
            aria-label={`Set color to ${c}`}
            onPointerDown={stopPointer}
            onClick={() => onColorChange(c)}
          />
        ))}
      </div>
      <button
        type="button"
        className={styles.deleteButton}
        onPointerDown={stopPointer}
        onClick={onDelete}
        aria-label="Delete note"
        title="Delete"
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <path
            d="M3 4h10M6 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1m1 0v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4h8z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
});
